/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import { Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { Swiper, SwiperSlide } from "swiper/react";
import { useForm } from "react-hook-form";

import { useGetPlansQuery } from "@/redux-store/api/marketingApi";

import Characteristics from "./SubscriptionPlanCard/Characteristics";
import FilterForm from "./FilterForm";
import PlansTable from "./PlansTable";
import Pricing from "./SubscriptionPlanCard/Pricing";
import Section from "@/components/Section";
import "./styles.css";
import {PaymentFrequencyEnum} from "@/types/enums.ts";

const SubscriptionPlans = () => {
  const isMobile = useMediaQuery("(max-width: 1023.98px)");
  const isTablet = useMediaQuery(
    "(min-width: 1024px) and (max-width: 1199.98px)"
  );
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const [paymentFrequency, setPaymentFrequency] = useState<PaymentFrequencyEnum>(PaymentFrequencyEnum.YEARLY);

  // Form control using React Hook Form
  const methods = useForm<IPlanFilterFields>({
    defaultValues: {
      countryId: 1,
      deviceId: 0,
    },
  });
  const { watch } = methods;
  const formDeviceId = watch().deviceId;
  const fromCountryId = watch().countryId;

  // Get plans based on chosen country and device
  const { data: plans, error } = useGetPlansQuery({
    countryId: fromCountryId || 1,
    deviceId: formDeviceId || 0,
  });

  // Index of the slide you want to begin with - is popular one
  const initialSlideIndex = plans?.allPlans.findIndex(
    ({ mostPopular }) => mostPopular
  );
  const swiperRef = useRef<any>(null);

  // Goes to first slide on product change
  useEffect(() => {
    if (isMobile && swiperRef.current) {
      const initialSlideIndex = plans?.allPlans.findIndex(
        ({ mostPopular }) => mostPopular
      );

      swiperRef.current.swiper.slideTo(initialSlideIndex);
    }
  }, [isMobile, plans?.allPlans]);

  const handleChangePaymentFrequency = (value: any) => {
    setPaymentFrequency(value);
  };

  return (
    <Section pt={{ xs: 0, md: 2, lg: 8 }} pb={{ xs: 5, md: 7, lg: 25 }}>

      <Typography
        variant="h2"
        textAlign={{ xs: "start", md: "center" }}
        mb={{ xs: 3, md: 5 }}
        sx={{
          textAlign: "start",
          fontFamily: "Sharp Sans",
          fontWeight: 700,
        }}
      >
        Choose Your Optimal Plan
      </Typography>

      <FilterForm methods={methods}  onChangeFrequency={handleChangePaymentFrequency} />

      {error && (
        <Typography variant="subtitle1" fontWeight={700} textAlign="center">
          There are no available plans for selected country.
        </Typography>
      )}

      {plans && !error && (
        <>
          {/* Mobile slider */}
          {isMobile && (
            <Box
              component={Swiper}
              ref={swiperRef}
              sx={{ maxWidth: "100%", mb: { xs: 3, lg: 0 } }}
              spaceBetween={4}
              centeredSlides={true}
              slidesPerView="auto"
              className="plan-swiper"
              initialSlide={initialSlideIndex}
            >
              {plans.allPlans.map((plan) => (
                <SwiperSlide key={plan.id}>
                  <Pricing
                    plan={plan}
                    paymentFrequency={paymentFrequency}
                  />
                  <Characteristics
                    features={plan.features}
                    planFeatures={plans.planFeatures}
                  />
                </SwiperSlide>
              ))}
            </Box>
          )}

          {/* Tablet - small desktop grids */}
          {isTablet && (
            <Grid container spacing={1}>
              {plans.allPlans.map((plan) => (
                <Grid item md={12 / plans.allPlans.length} key={plan.id}>
                  <Pricing
                    plan={plan}
                    paymentFrequency={paymentFrequency}
                  />
                  <Characteristics
                    features={plan.features}
                    planFeatures={plans.planFeatures}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {/* Desktop plans table */}
          {isDesktop && (
            <PlansTable
              allPlans={plans.allPlans}
              planFeatures={plans.planFeatures}
              paymentFrequency={paymentFrequency}
            />
          )}
        </>
      )}
    </Section>
  );
};

export default SubscriptionPlans;
